import { workAreaRoutePath } from '@featit/shared/addressing/paths';
import {
  parseFileReferenceFolder,
  type IFileReferenceFolder,
} from '@featit/shared/file-reference-folder';
import {
  repositoryFileRoutes,
  repositoryFolderFileRoutes,
} from '@featit/shared/repository-file/contract';

/**
 * U-0023 (`docs/concept-file-grants.md` §12.1): the file page's address,
 * `/<namespace>/<work-area>/-/files/<path>`. Each segment is encoded and the slashes kept, so the
 * address reads as the path does and a folder in it is a folder in the address.
 */
export function repositoryFilePath(
  workAreaRef: string | null | undefined,
  path: string,
  folder?: IFileReferenceFolder | null,
): string {
  const encoded = path.split('/').map(encodeURIComponent).join('/');
  // I-1774 (§12.9.2): a qualified reference's address names its folder and device, `@` kept so the
  // address reads as the reference does.
  const sub = folder
    ? `/folders/${encodeURIComponent(`${folder.name}@${folder.device}`).replace(/%40/g, '@')}/files/${encoded}`
    : `/files/${encoded}`;
  return workAreaRef ? workAreaRoutePath(workAreaRef, sub) : `/-${sub}`;
}

/** The folder an address names (`/-/folders/<folder>@<device>/files/…`), or `null` for a plain path. */
export function repositoryFileFolderFromPathname(pathname: string): IFileReferenceFolder | null {
  const match = /\/-\/folders\/([^/]+)\/files\//.exec(pathname);
  if (!match) return null;
  try {
    return parseFileReferenceFolder(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

/** The path a `files/*` route's splat names, decoded segment by segment; `null` when none. */
export function repositoryFilePathFromSplat(splat: string | undefined): string | null {
  if (!splat) return null;
  try {
    return splat
      .split('/')
      .map((segment) => decodeURIComponent(segment))
      .join('/');
  } catch {
    return null;
  }
}

/** The file path an address names: whatever follows its `/-/files/`, decoded; `null` when none. */
export function repositoryFilePathFromPathname(pathname: string): string | null {
  const folder = /\/-\/folders\/[^/]+\/files\//.exec(pathname);
  if (folder) return repositoryFilePathFromSplat(pathname.slice(folder.index + folder[0].length));
  const marker = '/-/files/';
  const at = pathname.indexOf(marker);
  if (at < 0) return null;
  return repositoryFilePathFromSplat(pathname.slice(at + marker.length));
}

/** The file routes of a work area (`repositoryFileRoutes`, shared with the app shell's table). */
export function repositoryFileRoutePaths(): string[] {
  return [...repositoryFileRoutes(), ...repositoryFolderFileRoutes()];
}
